import type { Analytics } from 'firebase/analytics';
import { sendLandingBeacon } from './lib/beacon';

// Firebase config (values come from .env — see .env.example)
const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// The Firebase runtime is imported only after analytics consent. Keeping these
// imports dynamic removes the Analytics SDK from the public page's critical
// bundle and, more importantly, guarantees it is never evaluated pre-consent.
let analytics: Analytics | null = null;
let analyticsSdk: typeof import('firebase/analytics') | null = null;
let analyticsInitialization: Promise<void> | null = null;

// Consent storage key
const CONSENT_KEY = 'cookie_consent';

// ============================================================================
// CONSENT MANAGEMENT
// ============================================================================

export interface ConsentPreferences {
    analytics: boolean;
    marketing: boolean;
    timestamp: number;
}

/**
 * Get stored consent preferences
 */
export function getConsentPreferences(): ConsentPreferences | null {
    try {
        const stored = localStorage.getItem(CONSENT_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Error reading consent preferences:', e);
    }
    return null;
}

/**
 * Save consent preferences and update analytics
 */
export function saveConsentPreferences(preferences: ConsentPreferences): void {
    try {
        localStorage.setItem(CONSENT_KEY, JSON.stringify(preferences));
        console.log('🍪 Consent saved:', preferences);

        // Update Google consent mode
        updateGoogleConsent(preferences);

        // Dispatch custom event for other components
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { detail: preferences }));
    } catch (e) {
        console.error('Error saving consent preferences:', e);
    }
}

/**
 * Update Google Consent Mode v2 (both Firebase SDK and gtag.js)
 */
function updateGoogleConsent(preferences: ConsentPreferences): void {
    // Update Firebase Analytics SDK consent
    if (analytics && analyticsSdk) {
        analyticsSdk.setConsent({
            analytics_storage: preferences.analytics ? 'granted' : 'denied',
            ad_storage: preferences.marketing ? 'granted' : 'denied',
            ad_user_data: preferences.marketing ? 'granted' : 'denied',
            ad_personalization: preferences.marketing ? 'granted' : 'denied',
        });
    }

    // Also update gtag.js consent IF a tag script is ever added (none is
    // loaded today — the guard below makes this a no-op until then). Read gtag off
    // globalThis so the presence check is a deterministic value lookup in
    // both the browser and unit tests, rather than relying on the ambient
    // `window` binding that a test cannot cleanly remove.
    const win = (globalThis as { window?: Window & { gtag?: (...args: unknown[]) => void } }).window;
    if (win?.gtag) {
        const gtag = win.gtag;
        gtag('consent', 'update', {
            'analytics_storage': preferences.analytics ? 'granted' : 'denied',
            'ad_storage': preferences.marketing ? 'granted' : 'denied',
            'ad_user_data': preferences.marketing ? 'granted' : 'denied',
            'ad_personalization': preferences.marketing ? 'granted' : 'denied'
        });
        console.log('📊 gtag consent updated:', preferences.analytics ? 'granted' : 'denied');
    }

    console.log('📊 Consent updated:', preferences.analytics ? 'granted' : 'denied');
}

/**
 * Check if user has given consent
 */
export function hasConsent(): boolean {
    return getConsentPreferences() !== null;
}

// ============================================================================
// FIREBASE ANALYTICS
// ============================================================================

/**
 * Check if we're in development/debug mode
 * Returns true if: localhost, 127.0.0.1, or ?analytics_debug=off in URL
 */
function isDevMode(): boolean {
    // Read the window off globalThis so the SSR/prerender guard is a plain
    // value check unit tests can drive deterministically (by deleting
    // globalThis.window) instead of depending on the ambient `window` binding.
    const win = (globalThis as { window?: Window }).window;
    if (!win) return false;
    const hostname = win.location.hostname;
    const params = new URLSearchParams(win.location.search);

    // Skip analytics on localhost or if debug param is set
    return hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        params.get('analytics_debug') === 'off';
}

/**
 * Initialize Firebase Analytics — GDPR opt-in.
 *
 * Analytics is OPT-IN: we do not load Firebase Analytics (getAnalytics), set
 * any analytics cookies, or send any hits until the user has explicitly
 * granted analytics consent via the cookie banner. This function is a no-op
 * until `preferences.analytics === true`, and is re-run when the user updates
 * consent (see the `cookieConsentUpdated` listener at the bottom of this file).
 */
async function startAnalytics(preferences: ConsentPreferences): Promise<void> {
    try {
        const [{ initializeApp }, sdk] = await Promise.all([
            import('firebase/app'),
            import('firebase/analytics'),
        ]);
        analyticsSdk = sdk;

        const supported = await sdk.isSupported();
        if (!supported) {
            console.warn('📊 Analytics: Not supported in this environment');
            return;
        }

        const app = initializeApp(firebaseConfig);
        // Set consent before creating Analytics so the first event observes
        // the stored opt-in state.
        sdk.setConsent({
            analytics_storage: 'granted',
            ad_storage: preferences.marketing ? 'granted' : 'denied',
            ad_user_data: preferences.marketing ? 'granted' : 'denied',
            ad_personalization: preferences.marketing ? 'granted' : 'denied',
        });
        analytics = sdk.getAnalytics(app);
        console.log('📊 Firebase Analytics: Initialized successfully');
        sdk.logEvent(analytics, 'page_view', { page_title: document.title });
        console.log('📊 Page view tracked');
        void import('./lib/interaction-vitals').then(({ observeInteractionVitals }) =>
            observeInteractionVitals((vital) => trackEvent('interaction_vital', {
                product: 'acesense', ...vital,
            }))).catch(() => undefined);
    } catch (e) {
        console.error('📊 Analytics: Failed to initialize', e);
    }
}

async function initializeAnalytics(): Promise<void> {
    // Skip analytics in dev mode
    if (isDevMode()) {
        console.log('📊 Analytics: Skipped (dev mode / localhost)');
        return;
    }

    // GDPR opt-in gate: do nothing until the user has granted analytics
    // consent. No SDK is loaded and no cookies are set before this point.
    const preferences = getConsentPreferences();
    if (!preferences?.analytics) {
        console.log('📊 Analytics: Not initialized (awaiting analytics consent)');
        return;
    }

    if (analytics) return;
    analyticsInitialization ??= startAnalytics(preferences);
    await analyticsInitialization;
}

/**
 * Track custom analytics events
 */
function trackEvent(eventName: string, params?: Record<string, unknown>): void {
    if (!analytics || !analyticsSdk) return;

    const preferences = getConsentPreferences();
    if (!preferences?.analytics) {
        console.log(`📊 Analytics: Event "${eventName}" not sent (no consent)`);
        return;
    }

    analyticsSdk.logEvent(analytics, eventName, params);
    console.log(`📊 Analytics: Event "${eventName}" tracked`, params);
}

export const AnalyticsEvents = {
    appStoreClick: (location: string) => {
        trackEvent('app_store_click', {
            store: 'ios',
            button_location: location
        });
        // First-party aggregate counter — cookieless, fires regardless of the
        // GA/PostHog consent gate above (see lib/beacon.ts).
        sendLandingBeacon('cta_click', { label: location });
    },

    webAppClick: (location: string) => {
        trackEvent('web_app_click', {
            destination: 'app.acesense.io',
            button_location: location
        });
        sendLandingBeacon('cta_click', { label: location });
    },
};

// Initialize analytics on module load. This is a no-op unless the user has
// already granted analytics consent in a previous session (opt-in gate above).
initializeAnalytics();

// When the user updates consent via the cookie banner, (re-)attempt analytics
// initialization. Loading only happens once consent is granted; withdrawing
// consent stops future events via the trackEvent() gate.
if (typeof window !== 'undefined') {
    window.addEventListener('cookieConsentUpdated', () => {
        if (!analytics) {
            initializeAnalytics();
        }
    });
}
